import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  WrenchScrewdriverIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../../stores/authStore';
import { api } from '../../../services/api';
import toast from 'react-hot-toast';

interface Equipment {
  _id: string;
  legacyId?: number;
  name: string;
  description?: string;
  category?: string;
  location?: string;
  brand?: string;
  modelName?: string;
  serialNumber?: string;
  purchaseDate?: string;
  warrantyExpires?: string;
  maintenanceSchedule?: string;
  isAvailable?: boolean;
  isPortable?: boolean;
  maxWeight?: number;
  imageName?: string;
  imageUrl?: string;
  images?: string[];
  manualUrl?: string;
  approved?: boolean;
  isPublic?: boolean;
  tags?: string[];
  notes?: string;
  createdBy?: string;
  updatedBy?: string;
  deletedBy?: string;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Cache for equipment data
let equipmentCache: Equipment[] | null = null;
let equipmentCacheTimestamp: number | null = null;
let equipmentPromise: Promise<Equipment[]> | null = null; // Prevent duplicate requests
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const EquipmentLibrary: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [allEquipment, setAllEquipment] = useState<Equipment[]>([]);
  const [filteredEquipment, setFilteredEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<Equipment | null>(null);

  const isConsultant = user?.roles?.includes('CONSULTANT');

  // Fetch all equipment with caching and duplicate request prevention
  const fetchAllEquipment = useCallback(async () => {
    // Check if we have valid cached data
    const now = Date.now();
    if (equipmentCache && equipmentCacheTimestamp && (now - equipmentCacheTimestamp) < CACHE_DURATION) {
      setAllEquipment(equipmentCache);
      setFilteredEquipment(equipmentCache);
      setLoading(false);
      return;
    }

    // If there's already a request in progress, wait for it
    if (equipmentPromise) {
      try {
        const equipment = await equipmentPromise;
        setAllEquipment(equipment);
        setFilteredEquipment(equipment);
        setLoading(false);
        return;
      } catch (error) {
        // If the existing promise failed, we'll make a new request below
        equipmentPromise = null;
      }
    }

    try {
      setLoading(true);
      
      // Create a new promise for this request
      equipmentPromise = api.get('/libraries/equipment').then((response: any) => {
        const equipment = response.data.equipment || [];
        
        // Update cache
        equipmentCache = equipment;
        equipmentCacheTimestamp = now;
        equipmentPromise = null; // Clear the promise
        
        return equipment;
      });
      
      const equipment = await equipmentPromise;
      setAllEquipment(equipment || []);
      setFilteredEquipment(equipment || []);
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
      toast.error('Failed to load equipment');
      setAllEquipment([]);
      setFilteredEquipment([]);
      equipmentPromise = null; // Clear the promise on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchAllEquipment();
  }, [fetchAllEquipment]);

  // Client-side filtering
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 3) {
      setFilteredEquipment(allEquipment);
      return;
    }

    const filtered = allEquipment.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.brand?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredEquipment(filtered);
  }, [searchTerm, allEquipment]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Update URL for meaningful searches
    if (value.length === 0 || value.length >= 3) {
      const newParams = new URLSearchParams(searchParams);
      if (value && value.length >= 3) {
        newParams.set('search', value);
      } else {
        newParams.delete('search');
      }
      window.history.replaceState({}, '', `${window.location.pathname}?${newParams}`);
    }
  };

  const toggleExpanded = (itemId: string) => {
    if (expandedItem === itemId) {
      setExpandedItem(null);
      setEditingItem(null);
    } else {
      setExpandedItem(itemId);
      const item = allEquipment.find(e => e._id === itemId);
      if (item && isConsultant) {
        setEditingItem({ ...item });
      }
    }
  };

  const handleInputChange = (field: keyof Equipment, value: any) => {
    if (editingItem) {
      setEditingItem({
        ...editingItem,
        [field]: value
      });
    }
  };

  const handleSave = async () => {
    if (!editingItem) return;

    try {
      await api.put(`/libraries/equipment/${editingItem._id}`, editingItem);
      toast.success('Equipment updated successfully');
      
      // Update local data and cache
      const updatedEquipment = allEquipment.map(item =>
        item._id === editingItem._id ? editingItem : item
      );
      setAllEquipment(updatedEquipment);
      
      // Update cache as well
      if (equipmentCache) {
        equipmentCache = updatedEquipment;
      }
      
      setExpandedItem(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to update equipment:', error);
      toast.error('Failed to update equipment');
    }
  };

  const handleCancel = () => {
    setEditingItem(null);
    setExpandedItem(null);
  };



  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/libraries" className="text-blue-600 text-sm font-medium">
            ← Libraries
          </Link>
          {isConsultant && (
            <button
              onClick={() => {/* TODO: Add new equipment modal */}}
              className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm font-medium"
            >
              + Add
            </button>
          )}
        </div>
        <h1 className="text-xl font-bold text-gray-900 mt-2">Equipment</h1>
        <p className="text-sm text-gray-600">
          {searchTerm && searchTerm.length >= 3 
            ? `${filteredEquipment.length} of ${allEquipment.length} items`
            : `${allEquipment.length} items`
          }
        </p>
      </div>

      {/* Mobile Search - Sticky positioned below top bar */}
      <div className="sticky top-16 z-30 px-4 py-3 bg-white border-b border-gray-200 shadow-lg">
        <div className="relative">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={searchTerm.length > 0 && searchTerm.length < 3 ? "Type 3+ characters to search..." : "Search equipment..."}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2 border-0 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white ${
              searchTerm.length > 0 && searchTerm.length < 3 
                ? 'bg-yellow-50 text-yellow-700' 
                : 'bg-gray-50'
            }`}
          />
        </div>
      </div>

      {/* Equipment List */}
      {filteredEquipment.length === 0 ? (
        <div className="text-center py-8 px-4">
          <WrenchScrewdriverIcon className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No equipment found</h3>
          <p className="mt-1 text-xs text-gray-500">
            {searchTerm
              ? 'Try adjusting your search'
              : 'Get started by adding equipment.'}
          </p>
        </div>
      ) : (
        <div className="bg-white divide-y divide-gray-100">
          {filteredEquipment.map((item) => (
            <div key={item._id}>
              {/* Mobile List Item */}
              <div
                onClick={() => toggleExpanded(item._id)}
                className="px-4 py-3 active:bg-gray-50 touch-manipulation"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{item.name}</h3>
                  </div>
                  {isConsultant && (
                    <PencilIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                  )}
                </div>
              </div>

              {/* Mobile Expanded Section */}
              {expandedItem === item._id && (
                <div className="px-4 py-4 bg-gray-50 border-t">
                  {isConsultant && editingItem ? (
                    /* Mobile Edit Form */
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={editingItem.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                          <input
                            type="text"
                            value={editingItem.category || ''}
                            onChange={(e) => handleInputChange('category', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
                          <input
                            type="text"
                            value={editingItem.location || ''}
                            onChange={(e) => handleInputChange('location', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Brand</label>
                          <input
                            type="text"
                            value={editingItem.brand || ''}
                            onChange={(e) => handleInputChange('brand', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Model</label>
                          <input
                            type="text"
                            value={editingItem.modelName || ''}
                            onChange={(e) => handleInputChange('modelName', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={editingItem.description || ''}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-700">Status</label>
                        <div className="flex flex-wrap gap-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.isAvailable || false}
                              onChange={(e) => handleInputChange('isAvailable', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Available</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editingItem.approved || false}
                              onChange={(e) => handleInputChange('approved', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Approved</span>
                          </label>
                        </div>
                      </div>

                      <div className="flex space-x-3 pt-2">
                        <button
                          onClick={handleCancel}
                          className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white active:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          className="flex-1 py-2 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 active:bg-blue-700"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Mobile View Details */
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-500">Status</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          item.isAvailable 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {item.isAvailable ? 'Available' : 'Unavailable'}
                        </span>
                      </div>

                      {(item.category || item.location) && (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {item.category && (
                            <div>
                              <span className="text-xs text-gray-500 block">Category</span>
                              <span className="text-gray-900">{item.category}</span>
                            </div>
                          )}
                          {item.location && (
                            <div>
                              <span className="text-xs text-gray-500 block">Location</span>
                              <span className="text-gray-900">{item.location}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {(item.brand || item.modelName) && (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {item.brand && (
                            <div>
                              <span className="text-xs text-gray-500 block">Brand</span>
                              <span className="text-gray-900">{item.brand}</span>
                            </div>
                          )}
                          {item.modelName && (
                            <div>
                              <span className="text-xs text-gray-500 block">Model</span>
                              <span className="text-gray-900">{item.modelName}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {item.description && (
                        <div>
                          <span className="text-xs text-gray-500 block mb-1">Description</span>
                          <p className="text-sm text-gray-900">{item.description}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EquipmentLibrary;